/**
=========================================================
* SBI-Fragebogen - Complete Implementation
* All 5 pages of the questionnaire
=========================================================
*/

import { useState, useEffect, useRef, createContext, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Checkbox from "@mui/material/Checkbox";
import LinearProgress from "@mui/material/LinearProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SbiHeader from "../../components/SbiHeader";
import { useMaterialUIController } from "context";
import colors, { SBI_DARK, SBI_LIGHT } from "assets/theme/base/colors";
import { useBranding, FRESENIUS } from "contextBranding";

// Branding palette context so shared components (NRSScale, YesNoQuestion, etc.) can use branding colors
const BrandPaletteContext = createContext(null);

function useBrandPalette() {
  const palette = useContext(BrandPaletteContext);
  return palette;
}

// Logo Component
function Logo() {
  return (
    <MDBox display="flex" alignItems="center">
      <img
        src={`${process.env.PUBLIC_URL}/logo_with_text_light.svg`}
        alt="Safe Brain Initiative"
        style={{ height: "48px", width: "auto" }}
      />
    </MDBox>
  );
}

// NRS Scale Component (0-10 scale)
function NRSScale({ value, onChange, id, minLabel = "0", maxLabel = "10" }) {
  const palette = useBrandPalette();
  const radioSx = palette
    ? {
        color: palette.radioUnchecked,
        "& .MuiSvgIcon-root": { color: "inherit", borderColor: "inherit" },
        "&.Mui-checked": {
          color: `${palette.radioChecked} !important`,
          "& .MuiSvgIcon-root": { borderColor: `${palette.radioChecked} !important` },
          "&:after": {
            opacity: "1 !important",
            background: `${palette.radioChecked} !important`,
            backgroundImage: "none !important",
          },
        },
        padding: 0.5,
      }
    : (theme) => ({
        color: `${theme.palette.backgroundSbi.default}80`,
        "&.Mui-checked": { color: theme.palette.backgroundSbi.secondary },
        "& .MuiSvgIcon-root": { color: "inherit" },
        padding: 0.5,
      });
  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" mb={1}>
        <MDTypography variant="caption" opacity={0.7}>
          {minLabel}
        </MDTypography>
        <MDTypography variant="caption" opacity={0.7}>
          {maxLabel}
        </MDTypography>
      </MDBox>
      <RadioGroup
        value={value}
        onChange={(e) => onChange(e.target.value)}
        row
        sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
      >
        {Array.from({ length: 11 }, (_, i) => i).map((num) => (
          <MDBox
            key={num}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Radio
              value={num.toString()}
              id={`${id}-${num}`}
              sx={radioSx}
            />
            <MDTypography
              variant="caption"
              sx={{ fontSize: "0.7rem", mt: 0.5 }}
            >
              {num}
            </MDTypography>
          </MDBox>
        ))}
      </RadioGroup>
    </MDBox>
  );
}

// Yes/No Question Component
function YesNoQuestion({ label, value, onChange, id }) {
  const { t } = useTranslation();
  const palette = useBrandPalette();
  const radioSx = palette
    ? {
        color: palette.radioUnchecked,
        "& .MuiSvgIcon-root": { color: "inherit", borderColor: "inherit" },
        "&.Mui-checked": {
          color: `${palette.radioChecked} !important`,
          "& .MuiSvgIcon-root": { borderColor: `${palette.radioChecked} !important` },
          "&:after": {
            opacity: "1 !important",
            background: `${palette.radioChecked} !important`,
            backgroundImage: "none !important",
          },
        },
      }
    : (theme) => ({
        color: `${theme.palette.backgroundSbi.default}80`,
        "&.Mui-checked": { color: theme.palette.backgroundSbi.secondary },
        "& .MuiSvgIcon-root": { color: "inherit" },
      });
  return (
    <MDBox>
      <MDTypography variant="button" fontWeight="medium" mb={1}>
        {label}
      </MDTypography>
      <RadioGroup value={value} onChange={(e) => onChange(e.target.value)} row>
        <FormControlLabel
          value="ja"
          control={<Radio sx={radioSx} />}
          label={
            <MDTypography variant="body2">
              {t("digitalAssessment.ja")}
            </MDTypography>
          }
        />
        <FormControlLabel
          value="nein"
          control={<Radio sx={radioSx} />}
          label={
            <MDTypography variant="body2">
              {t("digitalAssessment.nein")}
            </MDTypography>
          }
        />
      </RadioGroup>
    </MDBox>
  );
}

// Score Selector Component (0, 1, 2)
function ScoreSelector({ label, value, onChange, id }) {
  const palette = useBrandPalette();
  const scoreCardSx = palette
    ? { display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, backgroundColor: palette.scoreCardBg, borderRadius: 2 }
    : { display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, backgroundColor: "rgba(95, 119, 119, 0.2)", borderRadius: 2 };
  const radioSx = palette
    ? {
        color: palette.radioUnchecked,
        "& .MuiSvgIcon-root": { color: "inherit", borderColor: "inherit" },
        "&.Mui-checked": {
          color: `${palette.radioChecked} !important`,
          "& .MuiSvgIcon-root": { borderColor: `${palette.radioChecked} !important` },
          "&:after": {
            opacity: "1 !important",
            background: `${palette.radioChecked} !important`,
            backgroundImage: "none !important",
          },
        },
      }
    : (theme) => ({ color: `${theme.palette.backgroundSbi.default}80`, "&.Mui-checked": { color: theme.palette.backgroundSbi.secondary }, "& .MuiSvgIcon-root": { color: "inherit" } });
  return (
    <MDBox sx={scoreCardSx}>
      <MDTypography variant="body2" flex={1}>
        {label}
      </MDTypography>
      <RadioGroup value={value} onChange={(e) => onChange(e.target.value)} row>
        {["0", "1", "2"].map((opt) => (
          <FormControlLabel
            key={opt}
            value={opt}
            control={<Radio size="small" sx={radioSx} />}
            label={
              <MDTypography variant="caption">
                {opt}
              </MDTypography>
            }
          />
        ))}
      </RadioGroup>
    </MDBox>
  );
}

function DigitalAssessment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [controller] = useMaterialUIController();
  const { sbiDashboardTheme } = controller;
  const { isFreseniusBranding } = useBranding();
  const isLight = sbiDashboardTheme === "light";
  const theme = isLight ? SBI_LIGHT : SBI_DARK;
  const freseniusTheme = isLight ? FRESENIUS.themeLight : FRESENIUS.themeDark;
  const contentBg = isFreseniusBranding ? freseniusTheme.bgMain : theme.bgMain;
  const contentColor = isFreseniusBranding ? freseniusTheme.textPrimary : theme.textPrimary;

  const brand = useMemo(() => {
    if (isFreseniusBranding) {
      return {
        cardBg: isLight ? freseniusTheme.bgHeader : `${FRESENIUS.blueDark}ee`,
        cardBorder: isLight ? FRESENIUS.blue : freseniusTheme.border,
        titleGradient: isLight ? "#0a1628" : "#ffffff", /* dark blue in light mode, white in dark mode */
        textPrimary: freseniusTheme.textPrimary,
        sectionBorder: freseniusTheme.border,
        progressActive: FRESENIUS.blueLight,
        progressInactive: `${FRESENIUS.blueLight}4D`,
        radioChecked: FRESENIUS.blueLight,
        radioUnchecked: `${FRESENIUS.white}80`,
        scoreCardBg: isLight ? `${FRESENIUS.blueLight}33` : `${FRESENIUS.blueLight}22`,
        inputBg: isLight ? `${FRESENIUS.blueLight}40` : `${FRESENIUS.blueLight}30`,
        inputBorder: freseniusTheme.border,
        inputBorderHover: FRESENIUS.blueLight,
        inputColor: freseniusTheme.textPrimary,
        inputPlaceholder: `${freseniusTheme.textPrimary}B3`,
        primaryBtn: FRESENIUS.blue,
        primaryBtnHover: FRESENIUS.blueLight,
        primaryBtnDisabled: `${FRESENIUS.blueLight}4D`,
        primaryBtnColor: FRESENIUS.white,
        secondaryBtnBg: "transparent",
        secondaryBtnBorder: freseniusTheme.border,
        secondaryBtnColor: freseniusTheme.textPrimary,
        secondaryBtnHover: `${FRESENIUS.blueLight}22`,
        dialogBg: isLight ? freseniusTheme.bgHeader : `${FRESENIUS.blueDark}ee`,
        dialogBorder: freseniusTheme.border,
        dialogTitleColor: freseniusTheme.textPrimary,
        dialogTextColor: `${freseniusTheme.textPrimary}B3`,
        successBtnBg: FRESENIUS.blue,
        successBtnHover: FRESENIUS.blueLight,
        formBorder: freseniusTheme.border,
        formBorderHover: FRESENIUS.blueLight,
        checkboxChecked: FRESENIUS.blueLight,
        checkboxUnchecked: `${FRESENIUS.white}80`,
      };
    }
    const sb = colors.backgroundSbi;
    const acc = colors.accent;
    const prim = colors.primarySbi;
    const txt = theme.textPrimary;
    return {
      cardBg: isLight ? theme.bgHeader : `${sb.default}99`,
      cardBorder: isLight ? theme.border : `${sb.default}80`,
      titleGradient: isLight ? "#0a1628" : "#ffffff", /* dark blue in light mode, white in dark mode */
      textPrimary: txt,
      sectionBorder: isLight ? theme.border : `${sb.default}80`,
      progressActive: sb.secondary,
      progressInactive: `${sb.secondary}4D`,
      radioChecked: prim.main,
      radioUnchecked: `${sb.default}80`,
      scoreCardBg: "rgba(95, 119, 119, 0.2)",
      inputBg: `${sb.secondary}80`,
      inputBorder: `${sb.default}80`,
      inputBorderHover: sb.secondary,
      inputColor: theme.textSecondary || txt,
      inputPlaceholder: `${txt}B3`,
      primaryBtn: prim.main,
      primaryBtnHover: prim.focus,
      primaryBtnDisabled: `${sb.secondary}4D`,
      primaryBtnColor: colors.white?.main || "#fff",
      secondaryBtnBg: "transparent",
      secondaryBtnBorder: `${sb.default}80`,
      secondaryBtnColor: isLight ? txt : (colors.white?.main || "#fff"),
      secondaryBtnHover: `${sb.secondary}33`,
      dialogBg: isLight ? theme.bgHeader : `${sb.default}99`,
      dialogBorder: isLight ? theme.border : `${sb.default}80`,
      dialogTitleColor: isLight ? txt : (colors.white?.main || "#fff"),
      dialogTextColor: isLight ? (theme.textSecondary || `${txt}B3`) : `${colors.white?.main || "#fff"}B3`,
      successBtnBg: sb.secondary,
      successBtnHover: sb.default,
      formBorder: `${sb.default}80`,
      formBorderHover: sb.secondary,
      checkboxChecked: prim.main,
      checkboxUnchecked: `${sb.default}80`,
    };
  }, [isFreseniusBranding, isLight, theme, freseniusTheme]);

  const muiTheme = useTheme();
  const radioThemeOverride = useMemo(
    () =>
      createTheme(muiTheme, {
        components: {
          MuiRadio: {
            styleOverrides: {
              root: {
                "&:after": {
                  backgroundImage: "none",
                },
                "&.Mui-checked": {
                  "&:after": {
                    opacity: 1,
                    background: brand.radioChecked,
                    backgroundImage: "none",
                  },
                },
              },
              colorPrimary: {
                "&.Mui-checked": {
                  color: brand.radioChecked,
                  "& .MuiSvgIcon-root": {
                    borderColor: brand.radioChecked,
                  },
                },
              },
              colorSecondary: {
                "&.Mui-checked": {
                  color: brand.radioChecked,
                  "& .MuiSvgIcon-root": {
                    borderColor: brand.radioChecked,
                  },
                },
              },
            },
          },
          MuiCheckbox: {
            styleOverrides: {
              colorPrimary: {
                "&.Mui-checked": {
                  "& .MuiSvgIcon-root": {
                    borderColor: brand.checkboxChecked,
                  },
                },
              },
              colorSecondary: {
                "&.Mui-checked": {
                  "& .MuiSvgIcon-root": {
                    borderColor: brand.checkboxChecked,
                  },
                },
              },
            },
          },
        },
      }),
    [muiTheme, brand.radioChecked, brand.checkboxChecked]
  );

  const [step, setStep] = useState("patient-id"); // patient-id, page1, page2, page3, page4, page5
  const [dialogOpen, setDialogOpen] = useState(false);
  const [data, setData] = useState({
    patientId: "",
    // Page 1
    geschlecht: "",
    postOp: "",
    asaPs: "",
    chirurgischeDisziplin: [],
    artDerVersorgung: [],
    anaesthesiemethoden: [],
    praeventionsmassnahmen: [],
    zustimmung: "",
    // Page 2
    schmerzRuhe: "",
    schmerzMobil: "",
    shivering: "",
    rass: "",
    deliriumDesorientierung: "",
    deliriumVerhalten: "",
    deliriumKommunikation: "",
    deliriumHalluzination: "",
    deliriumRetardierung: "",
    monateRueckwaerts: "",
    angst: "",
    stress: "",
    wohlbefinden: "",
    blasenkatheter: "",
    uebelkeitNrs: "",
    erbrechen: "",
    sehhilfe: "",
    sehhilfeVorhanden: "",
    hoergeraet: "",
    hoergeraetVorhanden: "",
    zahnprothese: "",
    zahnprotheseVorhanden: "",
    fluessigkeitsnuechternheit: "",
    durstgefuehl: "",
    koerpertemperatur: "",
    laermreduktion: "",
    narkosezeitBeginn: "",
    narkosezeitEnde: "",
    atemwegsstrategie: [],
    burstSuppression: "",
    qualitaetsbewertung: "",
    zufriedenheit: "",
    // Page 3
    schmerzRuhe3: "",
    schmerzMobil3: "",
    shivering3: "",
    rass3: "",
    deliriumDesorientierung3: "",
    deliriumVerhalten3: "",
    deliriumKommunikation3: "",
    deliriumHalluzination3: "",
    deliriumRetardierung3: "",
    monateRueckwaerts3: "",
    angst3: "",
    stress3: "",
    wohlbefinden3: "",
    blasenkatheter3: "",
    uebelkeitNrs3: "",
    erbrechen3: "",
    sehhilfe3: "",
    sehhilfeVorhanden3: "",
    hoergeraet3: "",
    hoergeraetVorhanden3: "",
    zahnprothese3: "",
    zahnprotheseVorhanden3: "",
    durstgefuehl3: "",
    koerpertemperatur3: "",
    atemwegsbeschwerden3: "",
    qualitaetsbewertung3: "",
    zufriedenheit3: "",
    // Page 4
    schmerzRuhe4: "",
    schmerzMobil4: "",
    shivering4: "",
    rass4: "",
    deliriumDesorientierung4: "",
    deliriumVerhalten4: "",
    deliriumKommunikation4: "",
    deliriumHalluzination4: "",
    deliriumRetardierung4: "",
    monateRueckwaerts4: "",
    angst4: "",
    stress4: "",
    wohlbefinden4: "",
    blasenkatheter4: "",
    uebelkeitNrs4: "",
    erbrechen4: "",
    fluessigkeitPo: "",
    durstgefuehl4: "",
    koerpertemperatur4: "",
    laermreduktion4: "",
    atemwegsbeschwerden4: "",
    qualitaetsbewertung4: "",
    zufriedenheit4: "",
    // Page 5
    schmerzRuhe5: "",
    schmerzMobil5: "",
    shivering5: "",
    rass5: "",
    deliriumDesorientierung5: "",
    deliriumVerhalten5: "",
    deliriumKommunikation5: "",
    deliriumHalluzination5: "",
    deliriumRetardierung5: "",
    monateRueckwaerts5: "",
    angst5: "",
    stress5: "",
    wohlbefinden5: "",
    blasenkatheter5: "",
    uebelkeitNrs5: "",
    erbrechen5: "",
    durstgefuehl5: "",
    koerpertemperatur5: "",
    atemwegsbeschwerden5: "",
    qualitaetsbewertung5: "",
    zufriedenheit5: "",
  });

  // Scroll to top whenever the step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Also scroll the internal scrollable container
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [step]);

  const handleNext = () => {
    if (data.patientId.trim()) {
      setStep("page1");
    }
  };

  const handleBack = () => {
    navigate("/landing");
  };

  const handleCheckboxChange = (field, value, checked) => {
    setData((prev) => ({
      ...prev,
      [field]: checked
        ? [...prev[field], value]
        : prev[field].filter((v) => v !== value),
    }));
  };

  const updateData = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    console.log("Complete Form Data:", data);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    navigate("/landing");
  };

  const pages = ["page1", "page2", "page3", "page4", "page5"];
  const currentPageIndex = pages.indexOf(step);

  const renderPageHeader = (pageNum, title, subtitle) => (
    <MDBox
      p={3}
      sx={{ borderBottom: `1px solid ${brand.sectionBorder}` }}
    >
      <MDBox display="flex" alignItems="center" justifyContent="space-between">
        <MDBox>
          <MDTypography
            variant="h5"
            fontWeight="medium"
            sx={{
              background: brand.titleGradient,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {t("digitalAssessment.title")} {pageNum}/5
          </MDTypography>
          <MDTypography variant="body2" sx={{ color: brand.textPrimary, opacity: 0.7 }} mt={0.5}>
            {title}
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: brand.textPrimary, opacity: 0.6 }}>
            {subtitle}
          </MDTypography>
        </MDBox>
        <MDButton
          variant="text"
          sx={{ color: brand.textPrimary }}
          onClick={() => setStep("patient-id")}
          startIcon={<Icon>arrow_back</Icon>}
        >
          {t("digitalAssessment.zurId")}
        </MDButton>
      </MDBox>
      <MDBox display="flex" gap={1} mt={2}>
        {[1, 2, 3, 4, 5].map((num) => (
          <MDBox
            key={num}
            sx={{
              height: 8,
              flex: 1,
              borderRadius: 1,
              backgroundColor: num <= currentPageIndex + 1 ? brand.progressActive : brand.progressInactive,
              transition: "background-color 0.3s",
            }}
          />
        ))}
      </MDBox>
    </MDBox>
  );

  const renderNavButtons = (prevPage, nextPage, isLast = false) => (
    <MDBox
      display="flex"
      gap={2}
      pt={3}
      mt={3}
      sx={{ borderTop: `1px solid ${brand.sectionBorder}` }}
    >
      {prevPage && (
        <MDButton
          variant="gradient"
          color="info"
          size="large"
          onClick={() => setStep(prevPage)}
          startIcon={<Icon>arrow_back</Icon>}
          sx={{
            flex: 1,
            background: brand.successBtnBg,
            color: brand.primaryBtnColor,
            fontWeight: "600",
            "&:hover": { background: brand.successBtnHover },
          }}
        >
          {t("digitalAssessment.zurueck")}
        </MDButton>
      )}
      {isLast ? (
        <MDButton
          variant="contained"
          size="large"
          onClick={handleSubmit}
          endIcon={<Icon>check</Icon>}
          sx={{
            flex: 1,
            background: `${brand.primaryBtn} !important`,
            color: `${brand.primaryBtnColor} !important`,
            fontWeight: "600",
            border: "none !important",
            boxShadow: "none !important",
            "&:hover": {
              background: `${brand.primaryBtnHover} !important`,
              boxShadow: "none !important",
            },
          }}
        >
          Speichern und Fortfahren
        </MDButton>
      ) : nextPage ? (
        <MDButton
          variant="contained"
          size="large"
          onClick={() => setStep(nextPage)}
          endIcon={<Icon>arrow_forward</Icon>}
          sx={{
            flex: 1,
            background: `${brand.primaryBtn} !important`,
            color: `${brand.primaryBtnColor} !important`,
            fontWeight: "600",
            border: "none !important",
            boxShadow: "none !important",
            "&:hover": {
              background: `${brand.primaryBtnHover} !important`,
              boxShadow: "none !important",
            },
          }}
        >
          {t("common.weiter")}
        </MDButton>
      ) : null}
    </MDBox>
  );

  // Patient ID Screen
  if (step === "patient-id") {
    return (
      <DashboardLayout>
        <BrandPaletteContext.Provider value={brand}>
          <ThemeProvider theme={radioThemeOverride}>
          <SbiHeader />
          <MDBox
            sx={{
              backgroundColor: contentBg,
              color: contentColor,
              minHeight: "calc(100vh - 72px)",
              display: "flex",
              flexDirection: "column",
              "& .MuiTypography-root": { color: contentColor },
            }}
          >
          <MDBox
            width="100%"
            flex={1}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            p={3}
          >
            <Card
              sx={{
                maxWidth: 600,
                width: "100%",
                backgroundColor: brand.cardBg,
                backdropFilter: "blur(12px)",
                border: `1px solid ${brand.cardBorder}`,
                borderRadius: 4,
                p: 4,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
              }}
            >
              <MDBox textAlign="center" mb={4}>
                <MDTypography
                  variant="h4"
                  fontWeight="medium"
                  mb={1}
                  sx={{
                    background: brand.titleGradient,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {t("digitalAssessment.title")}
                </MDTypography>
                <MDTypography variant="body2" sx={{ color: brand.textPrimary, opacity: 0.8 }}>
                  {t("digitalAssessment.patientIdPrompt")}
                </MDTypography>
              </MDBox>

              <MDBox mb={3}>
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  mb={1}
                  sx={{ color: brand.textPrimary }}
                >
                  {t("digitalAssessment.patientId")}
                </MDTypography>
                <MDInput
                  type="text"
                  placeholder={t("digitalAssessment.patientIdPlaceholder")}
                  fullWidth
                  value={data.patientId}
                  onChange={(e) => updateData("patientId", e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && data.patientId.trim()) {
                      handleNext();
                    }
                  }}
                  sx={{
                    "& input": {
                      color: brand.inputColor,
                      fontSize: "1.1rem",
                      "&::placeholder": {
                        color: brand.inputPlaceholder,
                        opacity: 1,
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: brand.inputBg,
                      "& fieldset": { borderColor: brand.inputBorder },
                      "&:hover fieldset": { borderColor: brand.inputBorderHover },
                      "&.Mui-focused fieldset": { borderColor: brand.inputBorderHover },
                    },
                  }}
                />
              </MDBox>

              {/* Navigation Buttons */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <MDButton
                    onClick={handleBack}
                    fullWidth
                    size="large"
                    sx={{
                      background: brand.secondaryBtnBg,
                      color: brand.secondaryBtnColor,
                      fontWeight: "600",
                      fontSize: "1.1rem",
                      py: 1.5,
                      border: `2px solid ${brand.secondaryBtnBorder}`,
                      "&:hover": {
                        background: brand.secondaryBtnHover,
                        borderColor: brand.inputBorderHover,
                      },
                    }}
                  >
                    <Icon sx={{ mr: 1 }}>arrow_back</Icon>
                    {t("digitalAssessment.zurueck")}
                  </MDButton>
                </Grid>
                <Grid item xs={6}>
                  <MDButton
                    onClick={handleNext}
                    disabled={!data.patientId.trim()}
                    fullWidth
                    size="large"
                    sx={{
                      background: data.patientId.trim() ? brand.primaryBtn : brand.primaryBtnDisabled,
                      color: brand.primaryBtnColor,
                      fontWeight: "600",
                      fontSize: "1.1rem",
                      py: 1.5,
                      "&:hover": {
                        background: data.patientId.trim() ? brand.primaryBtnHover : brand.primaryBtnDisabled,
                      },
                    }}
                  >
                    {t("common.weiter")}
                    <Icon sx={{ ml: 1 }}>arrow_forward</Icon>
                  </MDButton>
                </Grid>
              </Grid>
            </Card>
          </MDBox>

          {/* Success Dialog */}
          <Dialog
            open={dialogOpen}
            onClose={(event, reason) => {
              if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
                handleDialogClose();
              }
            }}
            PaperProps={{
              sx: {
                backgroundColor: brand.dialogBg,
                backdropFilter: "blur(12px)",
                border: `1px solid ${brand.dialogBorder}`,
                borderRadius: 2,
                minWidth: 400,
              },
            }}
          >
            <DialogTitle sx={{ color: brand.dialogTitleColor, fontWeight: "600" }}>
              {t("digitalAssessment.erfolgreichGespeichert")}
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: brand.dialogTextColor }}>
                {t("digitalAssessment.erfolgreichGespeichertText")}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <MDButton
                onClick={handleDialogClose}
                sx={{
                  background: brand.successBtnBg,
                  color: brand.primaryBtnColor,
                  fontWeight: "600",
                  "&:hover": { background: brand.successBtnHover },
                }}
              >
                {t("common.ok")}
              </MDButton>
            </DialogActions>
          </Dialog>
        </MDBox>
        </ThemeProvider>
        </BrandPaletteContext.Provider>
      </DashboardLayout>
    );
  }

  // Page 1: Prämedikation
  if (step === "page1") {
    return (
      <DashboardLayout>
        <BrandPaletteContext.Provider value={brand}>
          <ThemeProvider theme={radioThemeOverride}>
          <SbiHeader
            rightContent={
              <MDTypography component="span" variant="body2" sx={{ color: contentColor, opacity: 0.9 }}>
                {t("digitalAssessment.patientId")}: <strong>{data.patientId}</strong>
              </MDTypography>
            }
          />
          <MDBox
            sx={{
              backgroundColor: contentBg,
              color: contentColor,
              minHeight: "calc(100vh - 72px)",
              display: "flex",
              flexDirection: "column",
              "& .MuiTypography-root": { color: contentColor },
            }}
          >
          <MDBox
            width="100%"
            flex={1}
            position="relative"
            p={{ xs: 2, md: 4 }}
          >
            <MDBox maxWidth="1200px" mx="auto">
              <Card
                sx={{
                  backgroundColor: brand.cardBg,
                  backdropFilter: "blur(12px)",
                  border: `1px solid ${brand.cardBorder}`,
                  borderRadius: 4,
                  overflow: "hidden",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                }}
              >
              {renderPageHeader(
                1,
                "Prämedikation",
                "Bitte beantworten Sie die folgenden Fragen",
              )}

              <MDBox
                ref={scrollContainerRef}
                p={3}
                sx={{ maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}
              >
                <Grid container spacing={3}>
                  {/* Question 30: Zustimmung */}
                  <Grid item xs={12}>
                    <MDBox
                      p={2}
                      sx={{
                        backgroundColor: "rgba(125, 201, 192, 0.15)",
                        borderRadius: 2,
                        border: (theme) =>
                          `1px solid ${brand.formBorderHover}`,
                      }}
                    >
                      <MDTypography
                        variant="button"
                        fontWeight="bold"
                        mb={2}
                      >
                        30. Zustimmung für SBI und Datenerfassung
                      </MDTypography>
                      <RadioGroup
                        value={data.zustimmung}
                        onChange={(e) =>
                          updateData("zustimmung", e.target.value)
                        }
                        row
                      >
                        {[
                              { value: "ja", label: t("digitalAssessment.ja") },
                              { value: "nein", label: t("digitalAssessment.nein") },
                            ].map((opt) => (
                          <FormControlLabel
                            key={opt.value}
                            value={opt.value}
                            control={
                              <Radio
                                sx={{
                                  color: brand.checkboxUnchecked,
                                  "& .MuiSvgIcon-root": { color: "inherit", borderColor: "inherit" },
                                  "&.Mui-checked": {
                                    color: brand.checkboxChecked,
                                    "& .MuiSvgIcon-root": { borderColor: brand.checkboxChecked },
                                  },
                                }}
                              />
                            }
                            label={
                              <MDTypography variant="body2">
                                {opt.label}
                              </MDTypography>
                            }
                          />
                        ))}
                      </RadioGroup>
                    </MDBox>
                  </Grid>

                  {/* Question 1: Geschlecht */}
                  <Grid item xs={12} md={6}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      1. Geschlecht
                    </MDTypography>
                    <RadioGroup
                      value={data.geschlecht}
                      onChange={(e) => updateData("geschlecht", e.target.value)}
                    >
                      {["Männlich", "Weiblich"].map((option) => (
                        <FormControlLabel
                          key={option}
                          value={option.toLowerCase()}
                          control={
                            <Radio
                              sx={{
                                color: (theme) =>
                                  brand.checkboxUnchecked,
                                "& .MuiSvgIcon-root": { color: "inherit", borderColor: "inherit" },
                                "&.Mui-checked": {
                                  color: brand.checkboxChecked,
                                  "& .MuiSvgIcon-root": { borderColor: brand.checkboxChecked },
                                },
                              }}
                            />
                          }
                          label={
                            <MDTypography variant="body2">
                              {option}
                            </MDTypography>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </Grid>

                  {/* Question 2: Post OP */}
                  <Grid item xs={12} md={6}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      2. Post OP
                    </MDTypography>
                    <RadioGroup
                      value={data.postOp}
                      onChange={(e) => updateData("postOp", e.target.value)}
                    >
                      {["Aufwachraum", "Station", "ICU", "PACU/IMC"].map(
                        (option) => (
                          <FormControlLabel
                            key={option}
                            value={option.toLowerCase()}
                            control={
                              <Radio
                                sx={{
                                  color: brand.checkboxUnchecked,
                                  "& .MuiSvgIcon-root": { color: "inherit", borderColor: "inherit" },
                                  "&.Mui-checked": {
                                    color: brand.checkboxChecked,
                                    "& .MuiSvgIcon-root": { borderColor: brand.checkboxChecked },
                                  },
                                }}
                              />
                            }
                            label={
                              <MDTypography variant="body2">
                                {option}
                              </MDTypography>
                            }
                          />
                        ),
                      )}
                    </RadioGroup>
                  </Grid>

                  {/* Question 3: ASA-PS */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      3. ASA-PS
                    </MDTypography>
                    <RadioGroup
                      value={data.asaPs}
                      onChange={(e) => updateData("asaPs", e.target.value)}
                      row
                    >
                      {["I", "II", "III", "IV", "V", "VI"].map((level) => (
                        <FormControlLabel
                          key={level}
                          value={level}
                          control={
                            <Radio
                              sx={{
                                color: (theme) =>
                                  brand.checkboxUnchecked,
                                "& .MuiSvgIcon-root": { color: "inherit", borderColor: "inherit" },
                                "&.Mui-checked": {
                                  color: brand.checkboxChecked,
                                  "& .MuiSvgIcon-root": { borderColor: brand.checkboxChecked },
                                },
                              }}
                            />
                          }
                          label={
                            <MDTypography
                              variant="body2"
                              fontFamily="monospace"
                            >
                              {level}
                            </MDTypography>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </Grid>

                  {/* Question 4: Chirurgische Disziplin */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      4. Chirurgische Disziplin
                    </MDTypography>
                    <Grid container spacing={1}>
                      {[
                        "Augenchirurgie",
                        "Herzchirurgie",
                        "Gefäßchirurgie",
                        "Kinderchirurgie",
                        "Gynäkologie",
                        "Bariatrie",
                        "Urologie",
                        "MKG-Chirurgie",
                        "Unfall-/Orthopädie",
                        "Thoraxchirurgie",
                        "Neurochirurgie",
                        "Plastische Chirurgie",
                        "HNO",
                        "Hüftfraktur",
                        "Viszeralchirurgie",
                        "Andere",
                      ].map((option) => (
                        <Grid item xs={6} sm={4} md={3} key={option}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={data.chirurgischeDisziplin.includes(
                                  option,
                                )}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    "chirurgischeDisziplin",
                                    option,
                                    e.target.checked,
                                  )
                                }
                                sx={{
                                  color: brand.checkboxUnchecked,
                                  "& .MuiSvgIcon-root": { color: "inherit", borderColor: "inherit" },
                                  "&.Mui-checked": {
                                    color: brand.checkboxChecked,
                                    "& .MuiSvgIcon-root": { borderColor: brand.checkboxChecked },
                                  },
                                }}
                              />
                            }
                            label={
                              <MDTypography variant="caption">
                                {option}
                              </MDTypography>
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>

                  {/* Question 5: Art der Versorgung */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      5. Art der Versorgung
                    </MDTypography>
                    <Grid container spacing={1}>
                      {[
                        { id: "a", label: "Stationär" },
                        { id: "b", label: "Ambulant" },
                        { id: "c", label: "Trauma" },
                        { id: "d", label: "Andere Intervention" },
                      ].map((option) => (
                        <Grid item xs={6} sm={3} key={option.id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={data.artDerVersorgung.includes(
                                  option.label,
                                )}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    "artDerVersorgung",
                                    option.label,
                                    e.target.checked,
                                  )
                                }
                                sx={{
                                  color: brand.checkboxUnchecked,
                                  "& .MuiSvgIcon-root": { color: "inherit", borderColor: "inherit" },
                                  "&.Mui-checked": {
                                    color: brand.checkboxChecked,
                                    "& .MuiSvgIcon-root": { borderColor: brand.checkboxChecked },
                                  },
                                }}
                              />
                            }
                            label={
                              <MDTypography variant="caption">
                                {option.id}. {option.label}
                              </MDTypography>
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>

                  {/* Question 6: Anästhesiemethoden */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      6. Anästhesiemethoden
                    </MDTypography>
                    <Grid container spacing={1}>
                      {[
                        "Allgemeinanästhesie",
                        "Sedierung",
                        "Regionalanästhesie",
                        "Balanciert",
                        "TCI",
                        "TIVA",
                        "EKT",
                      ].map((option) => (
                        <Grid item xs={6} sm={4} md={3} key={option}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={data.anaesthesiemethoden.includes(
                                  option,
                                )}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    "anaesthesiemethoden",
                                    option,
                                    e.target.checked,
                                  )
                                }
                                sx={{
                                  color: brand.checkboxUnchecked,
                                  "& .MuiSvgIcon-root": { color: "inherit", borderColor: "inherit" },
                                  "&.Mui-checked": {
                                    color: brand.checkboxChecked,
                                    "& .MuiSvgIcon-root": { borderColor: brand.checkboxChecked },
                                  },
                                }}
                              />
                            }
                            label={
                              <MDTypography variant="caption">
                                {option}
                              </MDTypography>
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>

                  {/* Question 7: Präventionsmaßnahmen */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      7. Präventionsmaßnahmen
                    </MDTypography>
                    <Grid container spacing={1}>
                      {[
                        "Kontinuierliche Temperaturmessung",
                        "Intraoperatives Nozizeptionsmonitoring",
                        "EEG verwendet",
                        "Benzodiazepin-Prämedikation",
                        "EMLA angeboten/bekommen",
                        "Verwendung von Kapnographie (bei Sedierungen)",
                        "Einbeziehung von Angehörigen (Einleitung, AWR)",
                        "Augenmasken und Ohrstöpseln angeboten",
                        "Vorbeugende Schmerztherapie (SOP) angewendet",
                        "Verwendung von Atropin",
                        'SOP "PONV" angewendet',
                        "Kontinuierliche Schmerztherapie",
                        "Sonstige",
                      ].map((option) => (
                        <Grid item xs={12} sm={6} key={option}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={data.praeventionsmassnahmen.includes(
                                  option,
                                )}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    "praeventionsmassnahmen",
                                    option,
                                    e.target.checked,
                                  )
                                }
                                sx={{
                                  color: brand.checkboxUnchecked,
                                  "& .MuiSvgIcon-root": { color: "inherit", borderColor: "inherit" },
                                  "&.Mui-checked": {
                                    color: brand.checkboxChecked,
                                    "& .MuiSvgIcon-root": { borderColor: brand.checkboxChecked },
                                  },
                                }}
                              />
                            }
                            label={
                              <MDTypography variant="caption">
                                {option}
                              </MDTypography>
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>

                {renderNavButtons(null, "page2")}
              </MDBox>
            </Card>
          </MDBox>
        </MDBox>

        {/* Success Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={(event, reason) => {
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              handleDialogClose();
            }
          }}
          PaperProps={{
            sx: {
              backgroundColor: (theme) =>
                brand.cardBg,
              backdropFilter: "blur(12px)",
              border: (theme) =>
                `1px solid ${brand.sectionBorder}`,
              borderRadius: 2,
              minWidth: 400,
            },
          }}
        >
          <DialogTitle
            sx={(theme) => ({
              color: brand.primaryBtnColor,
              fontWeight: "600",
            })}
          >
            {t("digitalAssessment.erfolgreichGespeichert")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              sx={(theme) => ({ color: `${brand.primaryBtnColor}B3` })}
            >
              {t("digitalAssessment.erfolgreichGespeichertText")}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <MDButton
              onClick={handleDialogClose}
              variant="gradient"
              color="info"
              sx={(theme) => ({
                background: brand.formBorderHover,
                color: brand.primaryBtnColor,
                fontWeight: "600",
                "&:hover": {
                  background: brand.successBtnHover,
                },
              })}
            >
              OK
            </MDButton>
          </DialogActions>
        </Dialog>
        </MDBox>
        </ThemeProvider>
        </BrandPaletteContext.Provider>
      </DashboardLayout>
    );
  }

  // Page 2: Operationssaal - Einleitung & Ausleitung
  if (step === "page2") {
    return (
      <DashboardLayout>
        <BrandPaletteContext.Provider value={brand}>
          <ThemeProvider theme={radioThemeOverride}>
          <SbiHeader
            rightContent={
              <MDTypography component="span" variant="body2" sx={{ color: contentColor, opacity: 0.9 }}>
                {t("digitalAssessment.patientId")}: <strong>{data.patientId}</strong>
              </MDTypography>
            }
          />
          <MDBox
            sx={{
              backgroundColor: contentBg,
              color: contentColor,
              minHeight: "calc(100vh - 72px)",
              display: "flex",
              flexDirection: "column",
              "& .MuiTypography-root": { color: contentColor },
            }}
          >
          <MDBox
            width="100%"
            flex={1}
            position="relative"
            p={{ xs: 2, md: 4 }}
          >
            <MDBox maxWidth="1200px" mx="auto">
              <Card
                sx={{
                  backgroundColor: brand.cardBg,
                  backdropFilter: "blur(12px)",
                  border: `1px solid ${brand.cardBorder}`,
                  borderRadius: 4,
                  overflow: "hidden",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                }}
              >
              {renderPageHeader(2, "Operationssaal", "Einleitung & Ausleitung")}

              <MDBox
                ref={scrollContainerRef}
                p={3}
                sx={{ maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}
              >
                <Grid container spacing={3}>
                  {/* Question 8: Schmerz */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      8. Schmerz (NRS 0-10 oder BPS 3-12)
                    </MDTypography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <MDTypography
                          variant="caption"
                          opacity={0.7}
                          mb={1}
                        >
                          In Ruhe
                        </MDTypography>
                        <NRSScale
                          value={data.schmerzRuhe}
                          onChange={(v) => updateData("schmerzRuhe", v)}
                          id="schmerz-ruhe"
                          minLabel="kein Schmerz"
                          maxLabel="max. Schmerz"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDTypography
                          variant="caption"
                          opacity={0.7}
                          mb={1}
                        >
                          Mobil
                        </MDTypography>
                        <NRSScale
                          value={data.schmerzMobil}
                          onChange={(v) => updateData("schmerzMobil", v)}
                          id="schmerz-mobil"
                          minLabel="kein Schmerz"
                          maxLabel="max. Schmerz"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Question 9: Shivering */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      9. Shivering (NRS 0-10)
                    </MDTypography>
                    <NRSScale
                      value={data.shivering}
                      onChange={(v) => updateData("shivering", v)}
                      id="shivering"
                      minLabel="kein Shivering"
                      maxLabel="max. Shivering"
                    />
                  </Grid>

                  {/* Question 10: RASS Skala */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      10. RASS Skala
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      opacity={0.7}
                      mb={2}
                    >
                      (Der Patient darf 30s vor Beurteilung nicht gestört
                      werden)
                    </MDTypography>
                    <RadioGroup
                      value={data.rass}
                      onChange={(e) => updateData("rass", e.target.value)}
                    >
                      <Grid container spacing={1}>
                        {[
                          { value: "4", label: "+4 Streitlustig, Aggressiv" },
                          { value: "3", label: "+3 sehr agitiert" },
                          { value: "2", label: "+2 agitiert" },
                          { value: "1", label: "+1 unruhig" },
                          { value: "0", label: "0 aufmerksam, ruhig" },
                          { value: "-1", label: "-1 schläfrig" },
                          { value: "-2", label: "-2 leichte Sedierung" },
                          { value: "-3", label: "-3 mäßige Sedierung" },
                          { value: "-4", label: "-4 tiefe Sedierung" },
                          { value: "-5", label: "-5 nicht erweckbar" },
                        ].map((opt) => (
                          <Grid item xs={12} sm={6} md={4} key={opt.value}>
                            <MDBox
                              p={1.5}
                              sx={{
                                backgroundColor: "rgba(95, 119, 119, 0.2)",
                                borderRadius: 1,
                              }}
                            >
                              <FormControlLabel
                                value={opt.value}
                                control={
                                  <Radio
                                    sx={{
                                      color: (theme) =>
                                        brand.checkboxUnchecked,
                                      "& .MuiSvgIcon-root": {
                                        color: "inherit",
                                        borderColor: "inherit",
                                      },
                                      "&.Mui-checked": {
                                        color: brand.checkboxChecked,
                                        "& .MuiSvgIcon-root": {
                                          borderColor: brand.checkboxChecked,
                                        },
                                      },
                                    }}
                                  />
                                }
                                label={
                                  <MDTypography variant="caption">
                                    {opt.label}
                                  </MDTypography>
                                }
                              />
                            </MDBox>
                          </Grid>
                        ))}
                      </Grid>
                    </RadioGroup>
                  </Grid>

                  {/* Question 11: Delirium (Nu-DESC) */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      11. Delirium (Nu-DESC)
                    </MDTypography>
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      <ScoreSelector
                        label="11.1 Desorientierung (Zeit, Ort, Person)"
                        value={data.deliriumDesorientierung}
                        onChange={(v) =>
                          updateData("deliriumDesorientierung", v)
                        }
                        id="delirium-des"
                      />
                      <ScoreSelector
                        label="11.2 Unangemessenes Verhalten (RASS+)"
                        value={data.deliriumVerhalten}
                        onChange={(v) => updateData("deliriumVerhalten", v)}
                        id="delirium-verh"
                      />
                      <ScoreSelector
                        label="11.3 Unangemessene Kommunikation"
                        value={data.deliriumKommunikation}
                        onChange={(v) => updateData("deliriumKommunikation", v)}
                        id="delirium-komm"
                      />
                      <ScoreSelector
                        label="11.4 Illusion/Halluzination"
                        value={data.deliriumHalluzination}
                        onChange={(v) => updateData("deliriumHalluzination", v)}
                        id="delirium-hall"
                      />
                      <ScoreSelector
                        label="11.5 Psychomotorische Verlangsamung (RASS-)"
                        value={data.deliriumRetardierung}
                        onChange={(v) => updateData("deliriumRetardierung", v)}
                        id="delirium-ret"
                      />
                    </MDBox>
                  </Grid>

                  {/* Question 12: Monate des Jahres rückwärts */}
                  <Grid item xs={12}>
                    <ScoreSelector
                      label="12. Monate des Jahres rückwärts"
                      value={data.monateRueckwaerts}
                      onChange={(v) => updateData("monateRueckwaerts", v)}
                      id="monate"
                    />
                  </Grid>

                  {/* Questions 13-15: Angst, Stress, Wohlbefinden */}
                  <Grid item xs={12} md={4}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      mb={1}
                    >
                      13. Angst (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.angst}
                      onChange={(v) => updateData("angst", v)}
                      id="angst"
                      minLabel="keine Angst"
                      maxLabel="max. Angst"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      mb={1}
                    >
                      14. Stress (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.stress}
                      onChange={(v) => updateData("stress", v)}
                      id="stress"
                      minLabel="kein Stress"
                      maxLabel="max. Stress"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      mb={1}
                    >
                      15. Wohlbefinden (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.wohlbefinden}
                      onChange={(v) => updateData("wohlbefinden", v)}
                      id="wohlbefinden"
                      minLabel="kein Wohlbefinden"
                      maxLabel="max. Wohlbefinden"
                    />
                  </Grid>

                  {/* Question 16: Blasenkatheter */}
                  <Grid item xs={12}>
                    <YesNoQuestion
                      label="16. Blasenkatheter"
                      value={data.blasenkatheter}
                      onChange={(v) => updateData("blasenkatheter", v)}
                      id="blasenkatheter"
                    />
                  </Grid>

                  {/* Question 17: Übelkeit & Erbrechen */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      17. Übelkeit & Erbrechen
                    </MDTypography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <MDTypography
                          variant="caption"
                          opacity={0.7}
                          mb={1}
                        >
                          Übelkeit NRS 0-10
                        </MDTypography>
                        <NRSScale
                          value={data.uebelkeitNrs}
                          onChange={(v) => updateData("uebelkeitNrs", v)}
                          id="uebelkeit"
                          minLabel="keine Übelkeit"
                          maxLabel="max. Übelkeit"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <YesNoQuestion
                          label="Erbrechen"
                          value={data.erbrechen}
                          onChange={(v) => updateData("erbrechen", v)}
                          id="erbrechen"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Questions 18-20: Sehhilfe, Hörgerät, Zahnprothese */}
                  <Grid item xs={12} md={4}>
                    <YesNoQuestion
                      label="18. Hat der Patient eine Sehhilfe?"
                      value={data.sehhilfe}
                      onChange={(v) => updateData("sehhilfe", v)}
                      id="sehhilfe"
                    />
                    {data.sehhilfe === "ja" && (
                      <MDBox mt={2}>
                        <YesNoQuestion
                          label="Sehhilfe vorhanden?"
                          value={data.sehhilfeVorhanden}
                          onChange={(v) => updateData("sehhilfeVorhanden", v)}
                          id="sehhilfe-vorh"
                        />
                      </MDBox>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <YesNoQuestion
                      label="19. Hat der Patient ein Hörgerät?"
                      value={data.hoergeraet}
                      onChange={(v) => updateData("hoergeraet", v)}
                      id="hoergeraet"
                    />
                    {data.hoergeraet === "ja" && (
                      <MDBox mt={2}>
                        <YesNoQuestion
                          label="Hörgerät vorhanden?"
                          value={data.hoergeraetVorhanden}
                          onChange={(v) => updateData("hoergeraetVorhanden", v)}
                          id="hoergeraet-vorh"
                        />
                      </MDBox>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <YesNoQuestion
                      label="20. Trägt der Patient Zahnprothesen?"
                      value={data.zahnprothese}
                      onChange={(v) => updateData("zahnprothese", v)}
                      id="zahnprothese"
                    />
                    {data.zahnprothese === "ja" && (
                      <MDBox mt={2}>
                        <YesNoQuestion
                          label="Zahnprothese vorhanden?"
                          value={data.zahnprotheseVorhanden}
                          onChange={(v) =>
                            updateData("zahnprotheseVorhanden", v)
                          }
                          id="zahnprothese-vorh"
                        />
                      </MDBox>
                    )}
                  </Grid>

                  {/* Question 21: Flüssigkeitsnüchternheit */}
                  <Grid item xs={12} md={6}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      21. Flüssigkeitsnüchternheit (h)
                    </MDTypography>
                    <MDInput
                      type="number"
                      placeholder="Stunden eingeben..."
                      value={data.fluessigkeitsnuechternheit}
                      onChange={(e) =>
                        updateData("fluessigkeitsnuechternheit", e.target.value)
                      }
                      sx={{
                        "& input": {
                          color: (theme) => brand.primaryBtnColor,
                          "&::placeholder": {
                            color: (theme) => `${brand.primaryBtnColor}B3`,
                            opacity: 1,
                          },
                        },
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: (theme) =>
                            `${brand.formBorderHover}80`,
                          "& fieldset": {
                            borderColor: (theme) =>
                              `${brand.sectionBorder}`,
                          },
                          "&:hover fieldset": {
                            borderColor: (theme) =>
                              brand.formBorderHover,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: (theme) =>
                              brand.formBorderHover,
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Question 22: Durstgefühl */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      22. Durstgefühl (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.durstgefuehl}
                      onChange={(v) => updateData("durstgefuehl", v)}
                      id="durst"
                      minLabel="kein Durstgefühl"
                      maxLabel="max. Durstgefühl"
                    />
                  </Grid>

                  {/* Question 23: Körpertemperatur */}
                  <Grid item xs={12} md={6}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      23. Körpertemperatur (C°)
                    </MDTypography>
                    <MDInput
                      type="number"
                      step="0.1"
                      placeholder="z.B. 36.5"
                      value={data.koerpertemperatur}
                      onChange={(e) =>
                        updateData("koerpertemperatur", e.target.value)
                      }
                      sx={{
                        "& input": {
                          color: (theme) => brand.primaryBtnColor,
                          "&::placeholder": {
                            color: (theme) => `${brand.primaryBtnColor}B3`,
                            opacity: 1,
                          },
                        },
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: (theme) =>
                            `${brand.formBorderHover}80`,
                          "& fieldset": {
                            borderColor: (theme) =>
                              `${brand.sectionBorder}`,
                          },
                          "&:hover fieldset": {
                            borderColor: (theme) =>
                              brand.formBorderHover,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: (theme) =>
                              brand.formBorderHover,
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Question 24: Lärmreduktion */}
                  <Grid item xs={12}>
                    <YesNoQuestion
                      label="24. Lärmreduktion durchgeführt?"
                      value={data.laermreduktion}
                      onChange={(v) => updateData("laermreduktion", v)}
                      id="laerm"
                    />
                  </Grid>

                  {/* Question 25: Narkosezeit */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      25. Narkosezeit (Uhrzeit)
                    </MDTypography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <MDTypography
                          variant="caption"
                          opacity={0.7}
                          mb={1}
                        >
                          Beginn Anästhesie
                        </MDTypography>
                        <MDInput
                          type="time"
                          value={data.narkosezeitBeginn}
                          onChange={(e) =>
                            updateData("narkosezeitBeginn", e.target.value)
                          }
                          sx={(theme) => ({
                            "& input": {
                              color: brand.primaryBtnColor,
                              "&::placeholder": {
                                color: `${brand.primaryBtnColor}B3`,
                                opacity: 1,
                              },
                            },
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: `${brand.sectionBorder}`,
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  brand.formBorderHover,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  brand.formBorderHover,
                              },
                            },
                          })}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDTypography
                          variant="caption"
                          opacity={0.7}
                          mb={1}
                        >
                          Nahtende
                        </MDTypography>
                        <MDInput
                          type="time"
                          value={data.narkosezeitEnde}
                          onChange={(e) =>
                            updateData("narkosezeitEnde", e.target.value)
                          }
                          sx={(theme) => ({
                            "& input": {
                              color: brand.primaryBtnColor,
                              "&::placeholder": {
                                color: `${brand.primaryBtnColor}B3`,
                                opacity: 1,
                              },
                            },
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: `${brand.sectionBorder}`,
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  brand.formBorderHover,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  brand.formBorderHover,
                              },
                            },
                          })}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Question 26a: Atemwegsstrategie */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      26a. Atemwegsstrategie (angewendet ankreuzen)
                    </MDTypography>
                    <Grid container spacing={1}>
                      {[
                        "Larynxmaske",
                        "Tubus",
                        "Videolaryngoskop",
                        "Muskelrelaxans",
                      ].map((option) => (
                        <Grid item xs={6} sm={3} key={option}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={data.atemwegsstrategie.includes(
                                  option,
                                )}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    "atemwegsstrategie",
                                    option,
                                    e.target.checked,
                                  )
                                }
                                sx={{
                                  color: brand.checkboxUnchecked,
                                  "& .MuiSvgIcon-root": { color: "inherit", borderColor: "inherit" },
                                  "&.Mui-checked": {
                                    color: brand.checkboxChecked,
                                    "& .MuiSvgIcon-root": { borderColor: brand.checkboxChecked },
                                  },
                                }}
                              />
                            }
                            label={
                              <MDTypography variant="caption">
                                {option}
                              </MDTypography>
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>

                  {/* Question 27: Burst Suppression */}
                  <Grid item xs={12} md={6}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      27. Gesamtzeit Burst Suppression (BIS)
                    </MDTypography>
                    <MDInput
                      type="text"
                      placeholder="z.B. 5 min 30 sek"
                      value={data.burstSuppression}
                      onChange={(e) =>
                        updateData("burstSuppression", e.target.value)
                      }
                      sx={{
                        "& input": {
                          color: (theme) => brand.primaryBtnColor,
                          "&::placeholder": {
                            color: (theme) => `${brand.primaryBtnColor}B3`,
                            opacity: 1,
                          },
                        },
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: (theme) =>
                            `${brand.formBorderHover}80`,
                          "& fieldset": {
                            borderColor: (theme) =>
                              `${brand.sectionBorder}`,
                          },
                          "&:hover fieldset": {
                            borderColor: (theme) =>
                              brand.formBorderHover,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: (theme) =>
                              brand.formBorderHover,
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Question 28: Qualitätsbewertung */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      28. Qualitätsbewertung der Patientenbefragung
                    </MDTypography>
                    <RadioGroup
                      value={data.qualitaetsbewertung}
                      onChange={(e) =>
                        updateData("qualitaetsbewertung", e.target.value)
                      }
                      row
                    >
                      {["Gut", "Mässig", "Schlecht"].map((opt) => (
                        <FormControlLabel
                          key={opt}
                          value={opt.toLowerCase()}
                          control={
                            <Radio
                              sx={{
                                color: (theme) =>
                                  brand.checkboxUnchecked,
                                "& .MuiSvgIcon-root": { color: "inherit", borderColor: "inherit" },
                                "&.Mui-checked": {
                                  color: brand.checkboxChecked,
                                  "& .MuiSvgIcon-root": { borderColor: brand.checkboxChecked },
                                },
                              }}
                            />
                          }
                          label={
                            <MDTypography variant="body2">
                              {opt}
                            </MDTypography>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </Grid>

                  {/* Question 29: Zufriedenheit */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      29. Zufriedenheit mit der Behandlung NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.zufriedenheit}
                      onChange={(v) => updateData("zufriedenheit", v)}
                      id="zufriedenheit"
                      minLabel="keine Zufriedenheit"
                      maxLabel="max. Zufriedenheit"
                    />
                  </Grid>
                </Grid>

                {renderNavButtons("page1", "page3")}
              </MDBox>
            </Card>
          </MDBox>
        </MDBox>

        {/* Success Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={(event, reason) => {
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              handleDialogClose();
            }
          }}
          PaperProps={{
            sx: {
              backgroundColor: (theme) =>
                brand.cardBg,
              backdropFilter: "blur(12px)",
              border: (theme) =>
                `1px solid ${brand.sectionBorder}`,
              borderRadius: 2,
              minWidth: 400,
            },
          }}
        >
          <DialogTitle
            sx={(theme) => ({
              color: brand.primaryBtnColor,
              fontWeight: "600",
            })}
          >
            {t("digitalAssessment.erfolgreichGespeichert")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              sx={(theme) => ({ color: `${brand.primaryBtnColor}B3` })}
            >
              {t("digitalAssessment.erfolgreichGespeichertText")}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <MDButton
              onClick={handleDialogClose}
              variant="gradient"
              color="info"
              sx={(theme) => ({
                background: brand.formBorderHover,
                color: brand.primaryBtnColor,
                fontWeight: "600",
                "&:hover": {
                  background: brand.successBtnHover,
                },
              })}
            >
              OK
            </MDButton>
          </DialogActions>
        </Dialog>
        </MDBox>
        </ThemeProvider>
        </BrandPaletteContext.Provider>
      </DashboardLayout>
    );
  }

  // Page 3: Übergabe Aufwachraum (similar structure to Page 2)
  if (step === "page3") {
    return (
      <DashboardLayout>
        <BrandPaletteContext.Provider value={brand}>
          <ThemeProvider theme={radioThemeOverride}>
          <SbiHeader
            rightContent={
              <MDTypography component="span" variant="body2" sx={{ color: contentColor, opacity: 0.9 }}>
                {t("digitalAssessment.patientId")}: <strong>{data.patientId}</strong>
              </MDTypography>
            }
          />
        <MDBox
          sx={{
            backgroundColor: contentBg,
            color: contentColor,
            minHeight: "calc(100vh - 72px)",
            display: "flex",
            flexDirection: "column",
            "& .MuiTypography-root": { color: contentColor },
          }}
        >
        <MDBox
          width="100%"
          flex={1}
          position="relative"
          p={{ xs: 2, md: 4 }}
        >
          <MDBox maxWidth="1200px" mx="auto">
            <Card
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              {renderPageHeader(3, "Übergabe Aufwachraum", "OP/Anästhesie")}

              <MDBox
                ref={scrollContainerRef}
                p={3}
                sx={{ maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}
              >
                <Grid container spacing={3}>
                  {/* Questions 8-29 with suffix "3" */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      8. Schmerz (NRS 0-10 oder BPS 3-12)
                    </MDTypography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <MDTypography
                          variant="caption"
                          opacity={0.7}
                          mb={1}
                        >
                          In Ruhe
                        </MDTypography>
                        <NRSScale
                          value={data.schmerzRuhe3}
                          onChange={(v) => updateData("schmerzRuhe3", v)}
                          id="schmerz-ruhe3"
                          minLabel="kein Schmerz"
                          maxLabel="max. Schmerz"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDTypography
                          variant="caption"
                          opacity={0.7}
                          mb={1}
                        >
                          Mobil
                        </MDTypography>
                        <NRSScale
                          value={data.schmerzMobil3}
                          onChange={(v) => updateData("schmerzMobil3", v)}
                          id="schmerz-mobil3"
                          minLabel="kein Schmerz"
                          maxLabel="max. Schmerz"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      9. Shivering (NRS 0-10)
                    </MDTypography>
                    <NRSScale
                      value={data.shivering3}
                      onChange={(v) => updateData("shivering3", v)}
                      id="shivering3"
                      minLabel="kein Shivering"
                      maxLabel="max. Shivering"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      10. RASS Skala
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      opacity={0.7}
                      mb={2}
                    >
                      (Der Patient darf 30s vor Beurteilung nicht gestört
                      werden)
                    </MDTypography>
                    <RadioGroup
                      value={data.rass3}
                      onChange={(e) => updateData("rass3", e.target.value)}
                    >
                      <Grid container spacing={1}>
                        {[
                          { value: "4", label: "+4 Streitlustig, Aggressiv" },
                          { value: "3", label: "+3 sehr agitiert" },
                          { value: "2", label: "+2 agitiert" },
                          { value: "1", label: "+1 unruhig" },
                          { value: "0", label: "0 aufmerksam, ruhig" },
                          { value: "-1", label: "-1 schläfrig" },
                          { value: "-2", label: "-2 leichte Sedierung" },
                          { value: "-3", label: "-3 mäßige Sedierung" },
                          { value: "-4", label: "-4 tiefe Sedierung" },
                          { value: "-5", label: "-5 nicht erweckbar" },
                        ].map((opt) => (
                          <Grid item xs={12} sm={6} md={4} key={opt.value}>
                            <MDBox
                              p={1.5}
                              sx={{
                                backgroundColor: "rgba(95, 119, 119, 0.2)",
                                borderRadius: 1,
                              }}
                            >
                              <FormControlLabel
                                value={opt.value}
                                control={
                                  <Radio
                                    sx={{
                                      color: brand.checkboxUnchecked,
                                      "&.Mui-checked": {
                                        color: brand.checkboxChecked,
                                      },
                                    }}
                                  />
                                }
                                label={
                                  <MDTypography variant="caption">
                                    {opt.label}
                                  </MDTypography>
                                }
                              />
                            </MDBox>
                          </Grid>
                        ))}
                      </Grid>
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      11. Delirium (Nu-DESC)
                    </MDTypography>
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      <ScoreSelector
                        label="11.1 Desorientierung (Zeit, Ort, Person)"
                        value={data.deliriumDesorientierung3}
                        onChange={(v) =>
                          updateData("deliriumDesorientierung3", v)
                        }
                        id="delirium-des3"
                      />
                      <ScoreSelector
                        label="11.2 Unangemessenes Verhalten (RASS+)"
                        value={data.deliriumVerhalten3}
                        onChange={(v) => updateData("deliriumVerhalten3", v)}
                        id="delirium-verh3"
                      />
                      <ScoreSelector
                        label="11.3 Unangemessene Kommunikation"
                        value={data.deliriumKommunikation3}
                        onChange={(v) =>
                          updateData("deliriumKommunikation3", v)
                        }
                        id="delirium-komm3"
                      />
                      <ScoreSelector
                        label="11.4 Illusion/Halluzination"
                        value={data.deliriumHalluzination3}
                        onChange={(v) =>
                          updateData("deliriumHalluzination3", v)
                        }
                        id="delirium-hall3"
                      />
                      <ScoreSelector
                        label="11.5 Psychomotorische Verlangsamung (RASS-)"
                        value={data.deliriumRetardierung3}
                        onChange={(v) => updateData("deliriumRetardierung3", v)}
                        id="delirium-ret3"
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <ScoreSelector
                      label="12. Monate des Jahres rückwärts"
                      value={data.monateRueckwaerts3}
                      onChange={(v) => updateData("monateRueckwaerts3", v)}
                      id="monate3"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      mb={1}
                    >
                      13. Angst (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.angst3}
                      onChange={(v) => updateData("angst3", v)}
                      id="angst3"
                      minLabel="keine Angst"
                      maxLabel="max. Angst"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      mb={1}
                    >
                      14. Stress (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.stress3}
                      onChange={(v) => updateData("stress3", v)}
                      id="stress3"
                      minLabel="kein Stress"
                      maxLabel="max. Stress"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      mb={1}
                    >
                      15. Wohlbefinden (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.wohlbefinden3}
                      onChange={(v) => updateData("wohlbefinden3", v)}
                      id="wohlbefinden3"
                      minLabel="kein Wohlbefinden"
                      maxLabel="max. Wohlbefinden"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <YesNoQuestion
                      label="16. Blasenkatheter"
                      value={data.blasenkatheter3}
                      onChange={(v) => updateData("blasenkatheter3", v)}
                      id="blasenkatheter3"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      17. Übelkeit & Erbrechen
                    </MDTypography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <MDTypography
                          variant="caption"
                          opacity={0.7}
                          mb={1}
                        >
                          Übelkeit NRS 0-10
                        </MDTypography>
                        <NRSScale
                          value={data.uebelkeitNrs3}
                          onChange={(v) => updateData("uebelkeitNrs3", v)}
                          id="uebelkeit3"
                          minLabel="keine Übelkeit"
                          maxLabel="max. Übelkeit"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <YesNoQuestion
                          label="Erbrechen"
                          value={data.erbrechen3}
                          onChange={(v) => updateData("erbrechen3", v)}
                          id="erbrechen3"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <YesNoQuestion
                      label="18. Hat der Patient eine Sehhilfe?"
                      value={data.sehhilfe3}
                      onChange={(v) => updateData("sehhilfe3", v)}
                      id="sehhilfe3"
                    />
                    {data.sehhilfe3 === "ja" && (
                      <MDBox mt={2}>
                        <YesNoQuestion
                          label="Sehhilfe vorhanden?"
                          value={data.sehhilfeVorhanden3}
                          onChange={(v) => updateData("sehhilfeVorhanden3", v)}
                          id="sehhilfe-vorh3"
                        />
                      </MDBox>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <YesNoQuestion
                      label="19. Hat der Patient ein Hörgerät?"
                      value={data.hoergeraet3}
                      onChange={(v) => updateData("hoergeraet3", v)}
                      id="hoergeraet3"
                    />
                    {data.hoergeraet3 === "ja" && (
                      <MDBox mt={2}>
                        <YesNoQuestion
                          label="Hörgerät vorhanden?"
                          value={data.hoergeraetVorhanden3}
                          onChange={(v) =>
                            updateData("hoergeraetVorhanden3", v)
                          }
                          id="hoergeraet-vorh3"
                        />
                      </MDBox>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <YesNoQuestion
                      label="20. Trägt der Patient Zahnprothesen?"
                      value={data.zahnprothese3}
                      onChange={(v) => updateData("zahnprothese3", v)}
                      id="zahnprothese3"
                    />
                    {data.zahnprothese3 === "ja" && (
                      <MDBox mt={2}>
                        <YesNoQuestion
                          label="Zahnprothese vorhanden?"
                          value={data.zahnprotheseVorhanden3}
                          onChange={(v) =>
                            updateData("zahnprotheseVorhanden3", v)
                          }
                          id="zahnprothese-vorh3"
                        />
                      </MDBox>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      22. Durstgefühl (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.durstgefuehl3}
                      onChange={(v) => updateData("durstgefuehl3", v)}
                      id="durst3"
                      minLabel="kein Durstgefühl"
                      maxLabel="max. Durstgefühl"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      23. Körpertemperatur (C°)
                    </MDTypography>
                    <MDInput
                      type="number"
                      step="0.1"
                      placeholder="z.B. 36.5"
                      value={data.koerpertemperatur3}
                      onChange={(e) =>
                        updateData("koerpertemperatur3", e.target.value)
                      }
                      sx={{
                        "& input": {
                          color: (theme) => brand.primaryBtnColor,
                          "&::placeholder": {
                            color: (theme) => `${brand.primaryBtnColor}B3`,
                            opacity: 1,
                          },
                        },
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: (theme) =>
                            `${brand.formBorderHover}80`,
                          "& fieldset": {
                            borderColor: (theme) =>
                              `${brand.sectionBorder}`,
                          },
                          "&:hover fieldset": {
                            borderColor: (theme) =>
                              brand.formBorderHover,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: (theme) =>
                              brand.formBorderHover,
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      26b. Atemwegsbeschwerden (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.atemwegsbeschwerden3}
                      onChange={(v) => updateData("atemwegsbeschwerden3", v)}
                      id="atemweg3"
                      minLabel="keine Beschwerden"
                      maxLabel="max. Beschwerden"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      28. Qualitätsbewertung der Patientenbefragung
                    </MDTypography>
                    <RadioGroup
                      value={data.qualitaetsbewertung3}
                      onChange={(e) =>
                        updateData("qualitaetsbewertung3", e.target.value)
                      }
                      row
                    >
                      {["Gut", "Mässig", "Schlecht"].map((opt) => (
                        <FormControlLabel
                          key={opt}
                          value={opt.toLowerCase()}
                          control={
                            <Radio
                              sx={{
                                color: (theme) =>
                                  brand.checkboxUnchecked,
                                "& .MuiSvgIcon-root": {
                                  color: "inherit",
                                  borderColor: "inherit",
                                },
                                "&.Mui-checked": {
                                  color: brand.checkboxChecked,
                                  "& .MuiSvgIcon-root": {
                                    borderColor: brand.checkboxChecked,
                                  },
                                },
                              }}
                            />
                          }
                          label={
                            <MDTypography variant="body2">
                              {opt}
                            </MDTypography>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      29. Zufriedenheit mit der Behandlung NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.zufriedenheit3}
                      onChange={(v) => updateData("zufriedenheit3", v)}
                      id="zufriedenheit3"
                      minLabel="keine Zufriedenheit"
                      maxLabel="max. Zufriedenheit"
                    />
                  </Grid>
                </Grid>

                {renderNavButtons("page2", "page4")}
              </MDBox>
            </Card>
          </MDBox>
        </MDBox>

        {/* Success Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={(event, reason) => {
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              handleDialogClose();
            }
          }}
          PaperProps={{
            sx: {
              backgroundColor: (theme) =>
                brand.cardBg,
              backdropFilter: "blur(12px)",
              border: (theme) =>
                `1px solid ${brand.sectionBorder}`,
              borderRadius: 2,
              minWidth: 400,
            },
          }}
        >
          <DialogTitle
            sx={(theme) => ({
              color: brand.primaryBtnColor,
              fontWeight: "600",
            })}
          >
            {t("digitalAssessment.erfolgreichGespeichert")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              sx={(theme) => ({ color: `${brand.primaryBtnColor}B3` })}
            >
              {t("digitalAssessment.erfolgreichGespeichertText")}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <MDButton
              onClick={handleDialogClose}
              variant="gradient"
              color="info"
              sx={(theme) => ({
                background: brand.formBorderHover,
                color: brand.primaryBtnColor,
                fontWeight: "600",
                "&:hover": {
                  background: brand.successBtnHover,
                },
              })}
            >
              OK
            </MDButton>
          </DialogActions>
        </Dialog>
        </MDBox>
        </ThemeProvider>
        </BrandPaletteContext.Provider>
      </DashboardLayout>
    );
  }

  // Page 4: Entlassung Aufwachraum (similar structure to Page 3)
  if (step === "page4") {
    return (
      <DashboardLayout>
        <BrandPaletteContext.Provider value={brand}>
          <ThemeProvider theme={radioThemeOverride}>
          <SbiHeader
            rightContent={
              <MDTypography component="span" variant="body2" sx={{ color: contentColor, opacity: 0.9 }}>
                {t("digitalAssessment.patientId")}: <strong>{data.patientId}</strong>
              </MDTypography>
            }
          />
        <MDBox
          sx={{
            backgroundColor: contentBg,
            color: contentColor,
            minHeight: "calc(100vh - 72px)",
            display: "flex",
            flexDirection: "column",
            "& .MuiTypography-root": { color: contentColor },
          }}
        >
        <MDBox
          width="100%"
          flex={1}
          position="relative"
          p={{ xs: 2, md: 4 }}
        >
          <MDBox maxWidth="1200px" mx="auto">
            <Card
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              {renderPageHeader(4, "Entlassung Aufwachraum", "Aufwachraum")}

              <MDBox
                ref={scrollContainerRef}
                p={3}
                sx={{ maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}
              >
                <Grid container spacing={3}>
                  {/* Questions 8-29 with suffix "4" */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      8. Schmerz (NRS 0-10 oder BPS 3-12)
                    </MDTypography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <MDTypography
                          variant="caption"
                          opacity={0.7}
                          mb={1}
                        >
                          In Ruhe
                        </MDTypography>
                        <NRSScale
                          value={data.schmerzRuhe4}
                          onChange={(v) => updateData("schmerzRuhe4", v)}
                          id="schmerz-ruhe4"
                          minLabel="kein Schmerz"
                          maxLabel="max. Schmerz"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDTypography
                          variant="caption"
                          opacity={0.7}
                          mb={1}
                        >
                          Mobil
                        </MDTypography>
                        <NRSScale
                          value={data.schmerzMobil4}
                          onChange={(v) => updateData("schmerzMobil4", v)}
                          id="schmerz-mobil4"
                          minLabel="kein Schmerz"
                          maxLabel="max. Schmerz"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      9. Shivering (NRS 0-10)
                    </MDTypography>
                    <NRSScale
                      value={data.shivering4}
                      onChange={(v) => updateData("shivering4", v)}
                      id="shivering4"
                      minLabel="kein Shivering"
                      maxLabel="max. Shivering"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      10. RASS Skala
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      opacity={0.7}
                      mb={2}
                    >
                      (Der Patient darf 30s vor Beurteilung nicht gestört
                      werden)
                    </MDTypography>
                    <RadioGroup
                      value={data.rass4}
                      onChange={(e) => updateData("rass4", e.target.value)}
                    >
                      <Grid container spacing={1}>
                        {[
                          { value: "4", label: "+4 Streitlustig, Aggressiv" },
                          { value: "3", label: "+3 sehr agitiert" },
                          { value: "2", label: "+2 agitiert" },
                          { value: "1", label: "+1 unruhig" },
                          { value: "0", label: "0 aufmerksam, ruhig" },
                          { value: "-1", label: "-1 schläfrig" },
                          { value: "-2", label: "-2 leichte Sedierung" },
                          { value: "-3", label: "-3 mäßige Sedierung" },
                          { value: "-4", label: "-4 tiefe Sedierung" },
                          { value: "-5", label: "-5 nicht erweckbar" },
                        ].map((opt) => (
                          <Grid item xs={12} sm={6} md={4} key={opt.value}>
                            <MDBox
                              p={1.5}
                              sx={{
                                backgroundColor: "rgba(95, 119, 119, 0.2)",
                                borderRadius: 1,
                              }}
                            >
                              <FormControlLabel
                                value={opt.value}
                                control={
                                  <Radio
                                    sx={{
                                      color: brand.checkboxUnchecked,
                                      "&.Mui-checked": {
                                        color: brand.checkboxChecked,
                                      },
                                    }}
                                  />
                                }
                                label={
                                  <MDTypography variant="caption">
                                    {opt.label}
                                  </MDTypography>
                                }
                              />
                            </MDBox>
                          </Grid>
                        ))}
                      </Grid>
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      11. Delirium (Nu-DESC)
                    </MDTypography>
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      <ScoreSelector
                        label="11.1 Desorientierung (Zeit, Ort, Person)"
                        value={data.deliriumDesorientierung4}
                        onChange={(v) =>
                          updateData("deliriumDesorientierung4", v)
                        }
                        id="delirium-des4"
                      />
                      <ScoreSelector
                        label="11.2 Unangemessenes Verhalten (RASS+)"
                        value={data.deliriumVerhalten4}
                        onChange={(v) => updateData("deliriumVerhalten4", v)}
                        id="delirium-verh4"
                      />
                      <ScoreSelector
                        label="11.3 Unangemessene Kommunikation"
                        value={data.deliriumKommunikation4}
                        onChange={(v) =>
                          updateData("deliriumKommunikation4", v)
                        }
                        id="delirium-komm4"
                      />
                      <ScoreSelector
                        label="11.4 Illusion/Halluzination"
                        value={data.deliriumHalluzination4}
                        onChange={(v) =>
                          updateData("deliriumHalluzination4", v)
                        }
                        id="delirium-hall4"
                      />
                      <ScoreSelector
                        label="11.5 Psychomotorische Verlangsamung (RASS-)"
                        value={data.deliriumRetardierung4}
                        onChange={(v) => updateData("deliriumRetardierung4", v)}
                        id="delirium-ret4"
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <ScoreSelector
                      label="12. Monate des Jahres rückwärts"
                      value={data.monateRueckwaerts4}
                      onChange={(v) => updateData("monateRueckwaerts4", v)}
                      id="monate4"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      mb={1}
                    >
                      13. Angst (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.angst4}
                      onChange={(v) => updateData("angst4", v)}
                      id="angst4"
                      minLabel="keine Angst"
                      maxLabel="max. Angst"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      mb={1}
                    >
                      14. Stress (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.stress4}
                      onChange={(v) => updateData("stress4", v)}
                      id="stress4"
                      minLabel="kein Stress"
                      maxLabel="max. Stress"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      mb={1}
                    >
                      15. Wohlbefinden (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.wohlbefinden4}
                      onChange={(v) => updateData("wohlbefinden4", v)}
                      id="wohlbefinden4"
                      minLabel="kein Wohlbefinden"
                      maxLabel="max. Wohlbefinden"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <YesNoQuestion
                      label="16. Blasenkatheter"
                      value={data.blasenkatheter4}
                      onChange={(v) => updateData("blasenkatheter4", v)}
                      id="blasenkatheter4"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      17. Übelkeit & Erbrechen
                    </MDTypography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <MDTypography
                          variant="caption"
                          opacity={0.7}
                          mb={1}
                        >
                          Übelkeit NRS 0-10
                        </MDTypography>
                        <NRSScale
                          value={data.uebelkeitNrs4}
                          onChange={(v) => updateData("uebelkeitNrs4", v)}
                          id="uebelkeit4"
                          minLabel="keine Übelkeit"
                          maxLabel="max. Übelkeit"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <YesNoQuestion
                          label="Erbrechen"
                          value={data.erbrechen4}
                          onChange={(v) => updateData("erbrechen4", v)}
                          id="erbrechen4"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <YesNoQuestion
                      label="21. Flüssigkeit p.o. (>200ml) - Hat Patient getrunken?"
                      value={data.fluessigkeitPo}
                      onChange={(v) => updateData("fluessigkeitPo", v)}
                      id="fluessigkeit-po"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      22. Durstgefühl (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.durstgefuehl4}
                      onChange={(v) => updateData("durstgefuehl4", v)}
                      id="durst4"
                      minLabel="kein Durstgefühl"
                      maxLabel="max. Durstgefühl"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      23. Körpertemperatur (C°)
                    </MDTypography>
                    <MDInput
                      type="number"
                      step="0.1"
                      placeholder="z.B. 36.5"
                      value={data.koerpertemperatur4}
                      onChange={(e) =>
                        updateData("koerpertemperatur4", e.target.value)
                      }
                      sx={{
                        "& input": {
                          color: (theme) => brand.primaryBtnColor,
                          "&::placeholder": {
                            color: (theme) => `${brand.primaryBtnColor}B3`,
                            opacity: 1,
                          },
                        },
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: (theme) =>
                            `${brand.formBorderHover}80`,
                          "& fieldset": {
                            borderColor: (theme) =>
                              `${brand.sectionBorder}`,
                          },
                          "&:hover fieldset": {
                            borderColor: (theme) =>
                              brand.formBorderHover,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: (theme) =>
                              brand.formBorderHover,
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <YesNoQuestion
                      label="24. Lärmreduktion durchgeführt?"
                      value={data.laermreduktion4}
                      onChange={(v) => updateData("laermreduktion4", v)}
                      id="laerm4"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      26b. Atemwegsbeschwerden (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.atemwegsbeschwerden4}
                      onChange={(v) => updateData("atemwegsbeschwerden4", v)}
                      id="atemweg4"
                      minLabel="keine Beschwerden"
                      maxLabel="max. Beschwerden"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      28. Qualitätsbewertung der Patientenbefragung
                    </MDTypography>
                    <RadioGroup
                      value={data.qualitaetsbewertung4}
                      onChange={(e) =>
                        updateData("qualitaetsbewertung4", e.target.value)
                      }
                      row
                    >
                      {["Gut", "Mässig", "Schlecht"].map((opt) => (
                        <FormControlLabel
                          key={opt}
                          value={opt.toLowerCase()}
                          control={
                            <Radio
                              sx={{
                                color: (theme) =>
                                  brand.checkboxUnchecked,
                                "& .MuiSvgIcon-root": {
                                  color: "inherit",
                                  borderColor: "inherit",
                                },
                                "&.Mui-checked": {
                                  color: brand.checkboxChecked,
                                  "& .MuiSvgIcon-root": {
                                    borderColor: brand.checkboxChecked,
                                  },
                                },
                              }}
                            />
                          }
                          label={
                            <MDTypography variant="body2">
                              {opt}
                            </MDTypography>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      29. Zufriedenheit mit der Behandlung NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.zufriedenheit4}
                      onChange={(v) => updateData("zufriedenheit4", v)}
                      id="zufriedenheit4"
                      minLabel="keine Zufriedenheit"
                      maxLabel="max. Zufriedenheit"
                    />
                  </Grid>
                </Grid>

                {renderNavButtons("page3", "page5")}
              </MDBox>
            </Card>
          </MDBox>
        </MDBox>

        {/* Success Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={(event, reason) => {
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              handleDialogClose();
            }
          }}
          PaperProps={{
            sx: {
              backgroundColor: (theme) =>
                brand.cardBg,
              backdropFilter: "blur(12px)",
              border: (theme) =>
                `1px solid ${brand.sectionBorder}`,
              borderRadius: 2,
              minWidth: 400,
            },
          }}
        >
          <DialogTitle
            sx={(theme) => ({
              color: brand.primaryBtnColor,
              fontWeight: "600",
            })}
          >
            {t("digitalAssessment.erfolgreichGespeichert")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              sx={(theme) => ({ color: `${brand.primaryBtnColor}B3` })}
            >
              {t("digitalAssessment.erfolgreichGespeichertText")}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <MDButton
              onClick={handleDialogClose}
              variant="gradient"
              color="info"
              sx={(theme) => ({
                background: brand.formBorderHover,
                color: brand.primaryBtnColor,
                fontWeight: "600",
                "&:hover": {
                  background: brand.successBtnHover,
                },
              })}
            >
              OK
            </MDButton>
          </DialogActions>
        </Dialog>
        </MDBox>
        </ThemeProvider>
        </BrandPaletteContext.Provider>
      </DashboardLayout>
    );
  }

  // Page 5: Station (Final page)
  if (step === "page5") {
    return (
      <DashboardLayout>
        <BrandPaletteContext.Provider value={brand}>
          <ThemeProvider theme={radioThemeOverride}>
          <SbiHeader
            rightContent={
              <MDTypography component="span" variant="body2" sx={{ color: contentColor, opacity: 0.9 }}>
                {t("digitalAssessment.patientId")}: <strong>{data.patientId}</strong>
              </MDTypography>
            }
          />
        <MDBox
          sx={{
            backgroundColor: contentBg,
            color: contentColor,
            minHeight: "calc(100vh - 72px)",
            display: "flex",
            flexDirection: "column",
            "& .MuiTypography-root": { color: contentColor },
          }}
        >
        <MDBox
          width="100%"
          flex={1}
          position="relative"
          p={{ xs: 2, md: 4 }}
        >
          <MDBox maxWidth="1200px" mx="auto">
            <Card
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              {renderPageHeader(5, "Station", "Abschlussbewertung")}

              <MDBox
                ref={scrollContainerRef}
                p={3}
                sx={{ maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}
              >
                <Grid container spacing={3}>
                  {/* Questions 8-29 with suffix "5" */}
                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      8. Schmerz (NRS 0-10 oder BPS 3-12)
                    </MDTypography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <MDTypography
                          variant="caption"
                          opacity={0.7}
                          mb={1}
                        >
                          In Ruhe
                        </MDTypography>
                        <NRSScale
                          value={data.schmerzRuhe5}
                          onChange={(v) => updateData("schmerzRuhe5", v)}
                          id="schmerz-ruhe5"
                          minLabel="kein Schmerz"
                          maxLabel="max. Schmerz"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDTypography
                          variant="caption"
                          opacity={0.7}
                          mb={1}
                        >
                          Mobil
                        </MDTypography>
                        <NRSScale
                          value={data.schmerzMobil5}
                          onChange={(v) => updateData("schmerzMobil5", v)}
                          id="schmerz-mobil5"
                          minLabel="kein Schmerz"
                          maxLabel="max. Schmerz"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      9. Shivering (NRS 0-10)
                    </MDTypography>
                    <NRSScale
                      value={data.shivering5}
                      onChange={(v) => updateData("shivering5", v)}
                      id="shivering5"
                      minLabel="kein Shivering"
                      maxLabel="max. Shivering"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      10. RASS Skala
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      opacity={0.7}
                      mb={2}
                    >
                      (Der Patient darf 30s vor Beurteilung nicht gestört
                      werden)
                    </MDTypography>
                    <RadioGroup
                      value={data.rass5}
                      onChange={(e) => updateData("rass5", e.target.value)}
                    >
                      <Grid container spacing={1}>
                        {[
                          { value: "4", label: "+4 Streitlustig, Aggressiv" },
                          { value: "3", label: "+3 sehr agitiert" },
                          { value: "2", label: "+2 agitiert" },
                          { value: "1", label: "+1 unruhig" },
                          { value: "0", label: "0 aufmerksam, ruhig" },
                          { value: "-1", label: "-1 schläfrig" },
                          { value: "-2", label: "-2 leichte Sedierung" },
                          { value: "-3", label: "-3 mäßige Sedierung" },
                          { value: "-4", label: "-4 tiefe Sedierung" },
                          { value: "-5", label: "-5 nicht erweckbar" },
                        ].map((opt) => (
                          <Grid item xs={12} sm={6} md={4} key={opt.value}>
                            <MDBox
                              p={1.5}
                              sx={{
                                backgroundColor: "rgba(95, 119, 119, 0.2)",
                                borderRadius: 1,
                              }}
                            >
                              <FormControlLabel
                                value={opt.value}
                                control={
                                  <Radio
                                    sx={{
                                      color: brand.checkboxUnchecked,
                                      "&.Mui-checked": {
                                        color: brand.checkboxChecked,
                                      },
                                    }}
                                  />
                                }
                                label={
                                  <MDTypography variant="caption">
                                    {opt.label}
                                  </MDTypography>
                                }
                              />
                            </MDBox>
                          </Grid>
                        ))}
                      </Grid>
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      11. Delirium (Nu-DESC)
                    </MDTypography>
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      <ScoreSelector
                        label="11.1 Desorientierung (Zeit, Ort, Person)"
                        value={data.deliriumDesorientierung5}
                        onChange={(v) =>
                          updateData("deliriumDesorientierung5", v)
                        }
                        id="delirium-des5"
                      />
                      <ScoreSelector
                        label="11.2 Unangemessenes Verhalten (RASS+)"
                        value={data.deliriumVerhalten5}
                        onChange={(v) => updateData("deliriumVerhalten5", v)}
                        id="delirium-verh5"
                      />
                      <ScoreSelector
                        label="11.3 Unangemessene Kommunikation"
                        value={data.deliriumKommunikation5}
                        onChange={(v) =>
                          updateData("deliriumKommunikation5", v)
                        }
                        id="delirium-komm5"
                      />
                      <ScoreSelector
                        label="11.4 Illusion/Halluzination"
                        value={data.deliriumHalluzination5}
                        onChange={(v) =>
                          updateData("deliriumHalluzination5", v)
                        }
                        id="delirium-hall5"
                      />
                      <ScoreSelector
                        label="11.5 Psychomotorische Verlangsamung (RASS-)"
                        value={data.deliriumRetardierung5}
                        onChange={(v) => updateData("deliriumRetardierung5", v)}
                        id="delirium-ret5"
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <ScoreSelector
                      label="12. Monate des Jahres rückwärts"
                      value={data.monateRueckwaerts5}
                      onChange={(v) => updateData("monateRueckwaerts5", v)}
                      id="monate5"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      mb={1}
                    >
                      13. Angst (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.angst5}
                      onChange={(v) => updateData("angst5", v)}
                      id="angst5"
                      minLabel="keine Angst"
                      maxLabel="max. Angst"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      mb={1}
                    >
                      14. Stress (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.stress5}
                      onChange={(v) => updateData("stress5", v)}
                      id="stress5"
                      minLabel="kein Stress"
                      maxLabel="max. Stress"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      mb={1}
                    >
                      15. Wohlbefinden (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.wohlbefinden5}
                      onChange={(v) => updateData("wohlbefinden5", v)}
                      id="wohlbefinden5"
                      minLabel="kein Wohlbefinden"
                      maxLabel="max. Wohlbefinden"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <YesNoQuestion
                      label="16. Blasenkatheter"
                      value={data.blasenkatheter5}
                      onChange={(v) => updateData("blasenkatheter5", v)}
                      id="blasenkatheter5"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      17. Übelkeit & Erbrechen
                    </MDTypography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <MDTypography
                          variant="caption"
                          opacity={0.7}
                          mb={1}
                        >
                          Übelkeit NRS 0-10
                        </MDTypography>
                        <NRSScale
                          value={data.uebelkeitNrs5}
                          onChange={(v) => updateData("uebelkeitNrs5", v)}
                          id="uebelkeit5"
                          minLabel="keine Übelkeit"
                          maxLabel="max. Übelkeit"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <YesNoQuestion
                          label="Erbrechen"
                          value={data.erbrechen5}
                          onChange={(v) => updateData("erbrechen5", v)}
                          id="erbrechen5"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      22. Durstgefühl (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.durstgefuehl5}
                      onChange={(v) => updateData("durstgefuehl5", v)}
                      id="durst5"
                      minLabel="kein Durstgefühl"
                      maxLabel="max. Durstgefühl"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      23. Körpertemperatur (C°)
                    </MDTypography>
                    <MDInput
                      type="number"
                      step="0.1"
                      placeholder="z.B. 36.5"
                      value={data.koerpertemperatur5}
                      onChange={(e) =>
                        updateData("koerpertemperatur5", e.target.value)
                      }
                      sx={{
                        "& input": {
                          color: (theme) => brand.primaryBtnColor,
                          "&::placeholder": {
                            color: (theme) => `${brand.primaryBtnColor}B3`,
                            opacity: 1,
                          },
                        },
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: (theme) =>
                            `${brand.formBorderHover}80`,
                          "& fieldset": {
                            borderColor: (theme) =>
                              `${brand.sectionBorder}`,
                          },
                          "&:hover fieldset": {
                            borderColor: (theme) =>
                              brand.formBorderHover,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: (theme) =>
                              brand.formBorderHover,
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      26b. Atemwegsbeschwerden (relativ) NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.atemwegsbeschwerden5}
                      onChange={(v) => updateData("atemwegsbeschwerden5", v)}
                      id="atemweg5"
                      minLabel="keine Beschwerden"
                      maxLabel="max. Beschwerden"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={1}
                    >
                      28. Qualitätsbewertung der Patientenbefragung
                    </MDTypography>
                    <RadioGroup
                      value={data.qualitaetsbewertung5}
                      onChange={(e) =>
                        updateData("qualitaetsbewertung5", e.target.value)
                      }
                      row
                    >
                      {["Gut", "Mässig", "Schlecht"].map((opt) => (
                        <FormControlLabel
                          key={opt}
                          value={opt.toLowerCase()}
                          control={
                            <Radio
                              sx={{
                                color: (theme) =>
                                  brand.checkboxUnchecked,
                                "& .MuiSvgIcon-root": {
                                  color: "inherit",
                                  borderColor: "inherit",
                                },
                                "&.Mui-checked": {
                                  color: brand.checkboxChecked,
                                  "& .MuiSvgIcon-root": {
                                    borderColor: brand.checkboxChecked,
                                  },
                                },
                              }}
                            />
                          }
                          label={
                            <MDTypography variant="body2">
                              {opt}
                            </MDTypography>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      mb={2}
                    >
                      29. Zufriedenheit mit der Behandlung NRS 0-10
                    </MDTypography>
                    <NRSScale
                      value={data.zufriedenheit5}
                      onChange={(v) => updateData("zufriedenheit5", v)}
                      id="zufriedenheit5"
                      minLabel="keine Zufriedenheit"
                      maxLabel="max. Zufriedenheit"
                    />
                  </Grid>
                </Grid>

                {renderNavButtons("page4", null, true)}
              </MDBox>
            </Card>
          </MDBox>
        </MDBox>

        {/* Success Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={(event, reason) => {
            // Prevent closing by clicking outside or pressing ESC
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              handleDialogClose();
            }
          }}
          PaperProps={{
            sx: {
              backgroundColor: (theme) =>
                brand.cardBg,
              backdropFilter: "blur(12px)",
              border: (theme) =>
                `1px solid ${brand.sectionBorder}`,
              borderRadius: 2,
              minWidth: 400,
            },
          }}
        >
          <DialogTitle
            sx={(theme) => ({
              color: brand.primaryBtnColor,
              fontWeight: "600",
            })}
          >
            {t("digitalAssessment.erfolgreichGespeichert")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              sx={(theme) => ({ color: `${brand.primaryBtnColor}B3` })}
            >
              {t("digitalAssessment.erfolgreichGespeichertText")}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <MDButton
              onClick={handleDialogClose}
              variant="gradient"
              color="info"
              sx={(theme) => ({
                background: brand.formBorderHover,
                color: brand.primaryBtnColor,
                fontWeight: "600",
                "&:hover": {
                  background: brand.successBtnHover,
                },
              })}
            >
              OK
            </MDButton>
          </DialogActions>
        </Dialog>
        </MDBox>
        </ThemeProvider>
        </BrandPaletteContext.Provider>
      </DashboardLayout>
    );
  }

  return null;
}

export default DigitalAssessment;
